import { apiTryFetchRetry } from '@editor/utils/apiFetchRetry';
import { isNonNullable } from '@editor/utils/types';
import analytics from '@repo/analytics';
import omit from 'lodash/omit';

import { USECASE_ALL_SLUG } from '../components/ElementPanels/TemplatesPanel/constants';
import { SEARCH_TEMPLATES } from '../global/analytics/events';
import { DEFAULT_TAKE } from '../global/constants';
import {
  Template,
  GenericApiResponse,
  Post,
  ApiPaginatedResponse,
} from '../types';

import api from './cmsContent';
import { USER_API_URL } from './constants';
import { getAccessToken } from './user';

type GetSearchTemplatesResponse = ApiPaginatedResponse<{
  query: string;
  templates: Template[];
  cached?: boolean;
}>;

type GetTemplatesResponse = ApiPaginatedResponse<{
  templates: Template[];
}>;

type ExploreResponse = ApiPaginatedResponse<{
  results: Post[];
  templates: Template[];
}>;

type RelatedResponse = GenericApiResponse<{
  results: Post[];
}>;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createAPI = ({ url }: { url: string | undefined }) => {
  const getTemplateSuggestions = async (
    useCaseSlug?: string
  ): Promise<
    | {
        authors: string[];
        names: string[];
        tags: string[];
        useCases: string[];
      }
    | { success: false; error: string }
  > => {
    return await apiTryFetchRetry(
      `${url}/v1/suggestions${useCaseSlug ? `?useCaseSlug=${useCaseSlug}` : ''}`
    );
  };

  // gets the templates of specified group
  const getGroupTemplates = async (
    groupId: string,
    take = 10,
    skip = 0,
    sort = 'name'
  ): Promise<
    | {
        templates: Template[];
        skip: number;
        take: number;
        sort: string | null;
      }
    | { success: false; error: string }
  > => {
    const result = await apiTryFetchRetry<{
      results: Post[];
      skip: number;
      take: number;
      sort: string | null;
    }>(
      `${url}/explore?take=${take}&skip=${skip}&sort=${sort}${
        groupId && groupId !== 'all' ? `&groupId=${groupId}` : ''
      }`
    );

    if ('error' in result) {
      return result;
    }

    const templates = result.results
      .map((post) => post.template)
      .filter(isNonNullable);

    const withBookmarks = await api.updateBookmarks(templates);
    return { ...result, templates: withBookmarks };
  };

  const getUseCaseTemplates = async (
    useCaseSlug: string,
    take = 10,
    skip = 0,
    sort = 'name',
    seed?: number
  ): Promise<GenericApiResponse<GetTemplatesResponse>> => {
    const result = await apiTryFetchRetry<GenericApiResponse<ExploreResponse>>(
      `${url}/v1/explore?take=${take}&skip=${skip}&sort=${sort}${useCaseSlug && useCaseSlug !== USECASE_ALL_SLUG ? `&useCaseSlug=${useCaseSlug}` : ''}${seed ? `&seed=${seed}` : ''}`
    );

    if (!result.success) {
      return result;
    }

    const templates = result.results
      .map((post) => ({ ...post.template, postId: post.id }))
      .filter(isNonNullable);

    const withBookmarks = await api.updateBookmarks(templates);
    return { ...result, templates: withBookmarks };
  };

  const getSearchTemplates = async (
    query: string,
    take = 10,
    skip = 0,
    sort = 'name',
    category: string,
    useCaseSlug?: string
  ): Promise<GetSearchTemplatesResponse> => {
    const result = await apiTryFetchRetry<
      GenericApiResponse<{
        skip: number;
        take: number;
        query: string;
        sort: string;
        results: Post[];
        cached?: boolean;
      }>
    >(
      `${url}/search?query=${encodeURIComponent(
        query
      )}&take=${take}&skip=${skip}&sort=${sort}${
        category?.length ? `&category=${category}` : ''
      }${useCaseSlug && useCaseSlug !== USECASE_ALL_SLUG ? `&useCaseSlug=${useCaseSlug}` : ''}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }
    );

    if (result?.success) {
      const templates = result.results
        .map((post) => post.template)
        .filter(isNonNullable);

      const modifiedResult = omit(result, ['results']);

      if (skip === 0) {
        analytics.track(SEARCH_TEMPLATES, {
          label: query,
          value: templates.length,
        });
      }

      return { ...modifiedResult, templates };
    }

    return { ...result };
  };

  const getRelated = async (
    postId: string,
    take = DEFAULT_TAKE,
    skip = 0
  ): Promise<GenericApiResponse<RelatedResponse>> => {
    return apiTryFetchRetry(
      `${url}/${postId}/related?take=${take}&skip=${skip}`
    );
  };

  return {
    getTemplateSuggestions,
    getGroupTemplates,
    getSearchTemplates,
    getUseCaseTemplates,
    getRelated,
  };
};

const postsApi = createAPI({ url: `${USER_API_URL}/posts` });
export default postsApi;
